import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const Reports = () => {
  const [activeTab, setActiveTab] = useState('monthly');
  const [monthlyData, setMonthlyData] = useState([]);
  const [employeePerformance, setEmployeePerformance] = useState([]);
  const [clientProfitability, setClientProfitability] = useState([]);
  const [conditionEffectiveness, setConditionEffectiveness] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    year: 2025,
    startDate: '',
    endDate: ''
  });

  const fetchReports = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'monthly':
          const monthlyRes = await axios.get(`/api/reports/monthly-revenue?year=${filters.year}`);
          setMonthlyData(monthlyRes.data);
          break;
        case 'employee':
          const employeeRes = await axios.get('/api/reports/employee-performance', {
            params: { startDate: filters.startDate, endDate: filters.endDate }
          });
          setEmployeePerformance(employeeRes.data);
          break;
        case 'client':
          const clientRes = await axios.get('/api/reports/client-profitability');
          setClientProfitability(clientRes.data);
          break;
        case 'condition':
          const conditionRes = await axios.get('/api/reports/condition-effectiveness');
          setConditionEffectiveness(conditionRes.data);
          break;
        default:
          break;
      }
    } catch (error) {
      toast.error('Error fetching reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [activeTab, filters]);

  const formatCurrency = (amount) => `$${amount.toLocaleString()}`;

  return (
    <div className="container-fluid">
      <h1 className="mb-4">Reports & Analytics</h1>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'monthly' ? 'active' : ''}`}
            onClick={() => setActiveTab('monthly')}
          >
            Monthly Revenue
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'employee' ? 'active' : ''}`}
            onClick={() => setActiveTab('employee')}
          >
            Employee Performance
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'client' ? 'active' : ''}`}
            onClick={() => setActiveTab('client')}
          >
            Client Profitability
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'condition' ? 'active' : ''}`}
            onClick={() => setActiveTab('condition')}
          >
            Condition Effectiveness
          </button>
        </li>
      </ul>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row">
            {activeTab === 'monthly' && (
              <div className="col-md-3">
                <label className="form-label">Year</label>
                <select 
                  className="form-select"
                  value={filters.year}
                  onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                >
                  {[2025, 2024, 2023, 2022].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            )}
            {activeTab === 'employee' && (
              <>
                <div className="col-md-3">
                  <label className="form-label">Start Date</label>
                  <input 
                    type="date"
                    className="form-control"
                    value={filters.startDate}
                    onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">End Date</label>
                  <input 
                    type="date"
                    className="form-control"
                    value={filters.endDate}
                    onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center p-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          {/* Monthly Revenue Report */}
          {activeTab === 'monthly' && (
            <div className="card">
              <div className="card-header">
                <h5>Monthly Revenue Report - {filters.year}</h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>Total Invoice</th>
                        <th>Caliber Revenue</th>
                        <th>Consultant Pay</th>
                        <th>Total Hours</th>
                        <th>Invoices</th>
                        <th>Margin %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyData.map(month => (
                        <tr key={month.month}>
                          <td>{month.month}</td>
                          <td>{formatCurrency(month.totalInvoice)}</td>
                          <td>{formatCurrency(month.totalRevenue)}</td>
                          <td>{formatCurrency(month.totalPay)}</td>
                          <td>{month.totalHours}</td>
                          <td>{month.invoiceCount}</td>
                          <td>{month.marginPercentage}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Employee Performance Report */}
          {activeTab === 'employee' && (
            <div className="card">
              <div className="card-header">
                <h5>Employee Performance Report</h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Client Rate</th>
                        <th>Total Invoice</th>
                        <th>Caliber Revenue</th>
                        <th>Total Hours</th>
                        <th>Avg Hours/Month</th>
                        <th>Margin %</th>
                        <th>Utilization</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employeePerformance.map(emp => (
                        <tr key={emp._id}>
                          <td>{emp.name}</td>
                          <td>${emp.clientRate}</td>
                          <td>{formatCurrency(emp.totalInvoice)}</td>
                          <td>{formatCurrency(emp.totalRevenue)}</td>
                          <td>{emp.totalHours}</td>
                          <td>{emp.avgHoursPerMonth}</td>
                          <td>{emp.marginPercentage}%</td>
                          <td>{(emp.utilization * 100).toFixed(1)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Client Profitability Report */}
          {activeTab === 'client' && (
            <div className="card">
              <div className="card-header">
                <h5>Client Profitability Report</h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Client</th>
                        <th>Total Invoice</th>
                        <th>Caliber Revenue</th>
                        <th>Total Hours</th>
                        <th>Employees</th>
                        <th>Margin %</th>
                        <th>Avg Revenue/Employee</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientProfitability.map(client => (
                        <tr key={client._id}>
                          <td>{client.clientName}</td>
                          <td>{formatCurrency(client.totalInvoice)}</td>
                          <td>{formatCurrency(client.totalRevenue)}</td>
                          <td>{client.totalHours}</td>
                          <td>{client.employeeCount}</td>
                          <td>{client.marginPercentage}%</td>
                          <td>{formatCurrency(client.avgRevenuePerEmployee)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Condition Effectiveness Report */}
          {activeTab === 'condition' && (
            <div className="card">
              <div className="card-header">
                <h5>Condition Effectiveness Report</h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Condition</th>
                        <th>Total Invoice</th>
                        <th>Caliber Revenue</th>
                        <th>Employees</th>
                        <th>Avg Margin %</th>
                        <th>Revenue/Employee</th>
                      </tr>
                    </thead>
                    <tbody>
                      {conditionEffectiveness.map(condition => (
                        <tr key={condition._id}>
                          <td>{condition.conditionName}</td>
                          <td>{formatCurrency(condition.totalInvoice)}</td>
                          <td>{formatCurrency(condition.totalRevenue)}</td>
                          <td>{condition.employeeCount}</td>
                          <td>{condition.avgMarginPercentage}%</td>
                          <td>{formatCurrency(condition.revenuePerEmployee)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Reports;